import { getInfoAsync } from 'expo-file-system/legacy'
import * as mime from 'react-native-mime-types'
import I18n from 'APP/I18n'

type Asset = {
  uri: string
  type?: string
  fileSize?: number
  size?: number
  fileName?: string
}

type Results = {
  errorCode?: string
  didCancel?: boolean
  assets: Asset[]
}

export const MAX_WIDTH_UPLOAD = 6048
export const MAX_HEIGHT_UPLOAD = 4032
/**
 * Returns the first asset's information.
 * The library does provide the option to select multiple images,
 * but the user is only allowed to upload one.
 */
export const getImageFromResult = (res: Results) => {
  if (!res.errorCode && !res.didCancel && (res?.assets?.length ?? 0) > 0) {
    return res.assets[0]
  } else {
    return null
  }
}

/**
 * Returns in bytes unless stated.
 *
 * Different libraries are used to upload (images vs PDFs),
 * and each returns different properties for the "same value".
 *
 * Example: "res.size" for PDFs vs "res.fileSize" for images.
 */

export const getFileSize = async (res: Asset | null) => {
  if (!res) {
    return 0
  }

  if (res.type === 'application/pdf') {
    return res.size ?? res.fileSize ?? 0
  }

  if (!res.uri) {
    return 0
  }

  const info = await getInfoAsync(res.uri)
  if (info?.exists && info.size != null) {
    return info.size
  }

  if (res.fileSize != null) {
    return res.fileSize
  }

  return 0
}

/**
 * Returns name of the file
 *
 * It is (unfortunately) optional for Expo ImagePicker to return the `fileName`, but `uri` is always returned and can be used to extract the file name.
 * @see https://docs.expo.dev/versions/latest/sdk/imagepicker/#imagepickerasset
 */
export const getFileName = (asset: Asset) => {
  return (
    asset?.fileName ||
    asset?.uri?.substring(asset?.uri?.lastIndexOf('/') + 1, asset?.uri?.length) ||
    ''
  )
}

//Scale to fit area and dimensions

export const resizeToFit = ({
  maxWidth,
  maxHeight,
  width,
  height,
}: {
  maxWidth: number
  maxHeight: number
  width: number
  height: number
}) => {
  const scalingRatio = Math.min(1, Math.sqrt((maxWidth * maxHeight) / (width * height)))
  const targetWidth = Math.floor(width * scalingRatio)
  const targetHeight = Math.floor(height * scalingRatio)
  return { targetWidth, targetHeight }
}

/**
 * Converts file size from bytes to KB or MB.
 * @param {number} sizeInBytes - The size in bytes to convert.
 * @param {'KB' | 'MB'} convertTo - The unit to convert to, either 'KB' or 'MB'.
 * @returns {string} The converted file size as a string with two decimal places.
 */
export const convertFileSize = (sizeInBytes: number, convertTo?: 'KB' | 'MB'): string => {
  const PER_KB = 1024
  const PER_MB = 1024 * 1024

  if (sizeInBytes == 0 || sizeInBytes == null || isNaN(sizeInBytes) || sizeInBytes < 0) {
    return I18n.t('ImagePicker.unknownSize')
  }

  // Determine unit and conversion factor
  const unit = convertTo ?? (sizeInBytes >= PER_MB ? 'MB' : 'KB')
  const divisor = unit === 'MB' ? PER_MB : PER_KB

  // Perform conversion and format result
  const convertedSize = (sizeInBytes / divisor).toFixed(2)
  return `${convertedSize} ${unit}`
}

export const getMimeType = (
  filename: string,
  assetType: string | null | undefined,
  fallback: string
) => {
  if (assetType !== null && assetType?.includes('/') && assetType.length > 0) {
    return assetType
  }
  const inferred = mime.lookup(filename)
  return inferred || fallback
}
